import { Component, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { MetaService } from '@ngx-meta/core';
import { RackeoLocaleService } from '../shared/services/locale.service';
import { CanonicalService } from '../shared/services/canonical.service';
import type { RackeoHostingResellerData, RackeoHostingResellerPlan } from '../shared/services/locale.service';
import { hrPlanPanelExpand } from './hosting-reseller.animations';
import { RACKEO_SITE } from '../shared/rackeo-site';
import { RackeoCartService } from '../shared/services/cart.service';

export interface ResellerComparisonRow {
  section?: string;
  feature?: string;
  helpText?: string;
  v?: [string, string, string, string];
}

@Component({
  selector: 'app-hosting-reseller',
  templateUrl: './hosting-reseller.component.html',
  styleUrls: ['./hosting-reseller.component.scss'],
  animations: [hrPlanPanelExpand]
})
export class HostingResellerComponent implements OnInit {
  expandedPlanIds: Record<string, boolean> = {};

  activeTab = 0;
  readonly tabPills = ['Generales', 'Características', 'Comparar planes'];
  readonly tabSectionTitles = ['GENERAL', 'CARACTERISTICAS', 'COMPARAR PLANES'];

  plans: RackeoHostingResellerPlan[] = [];
  comparisonPrices: RackeoHostingResellerData['comparisonPrices'] = [];
  comparisonRows: ResellerComparisonRow[] = [];
  heroImage = 'assets/images/rackeo/hosting-reseller/hero/banner-reseller.png';
  heroAlt = 'Comienza tu propio negocio Hosting Reseller';

  readonly planBullets: string[] = [
    'Site Publisher incluido: crea tu sitio en minutos.',
    'Todos los planes incluyen cPanel y WHM.',
    'Transferencia mensual ilimitada.',
    'Bases de datos ilimitadas.',
    'DNS anónimo y personalizable.'
  ];

  private readonly comparisonData: ResellerComparisonRow[] = [
    { section: 'ESPACIO WEB' },
    {
      feature: 'Espacio en el Disco',
      helpText: 'Espacio disponible para almacenar archivos web',
      v: ['40 GB', '80 GB', '120 GB', '160 GB']
    },
    {
      feature: 'Transferencia Mensual',
      helpText: 'Cantidad de información transferida desde o hacia el sitio o servidor',
      v: ['ILIMITADA', 'ILIMITADA', 'ILIMITADA', 'ILIMITADA']
    },
    {
      feature: 'WHM / cPanel anónimo y personalizable',
      helpText: 'Panel para controlar tu cuenta y las cuentas de tus clientes',
      v: ['check', 'check', 'check', 'check']
    },
    {
      feature: 'Cuentas de Hosting',
      helpText: 'Cantidad de clientes que puede tener',
      v: ['ILIMITADAS', 'ILIMITADAS', 'ILIMITADAS', 'ILIMITADAS']
    },
    { section: 'EXTRAS' },
    {
      feature: 'Softaculous',
      helpText: 'Herramientas de uso libre para crear y mejorar sitios',
      v: ['check', 'check', 'check', 'check']
    },
    {
      feature: 'Site Publisher',
      helpText: 'Crea tu sitio en minutos sin conocimientos avanzados',
      v: ['check', 'check', 'check', 'check']
    },
    { section: 'SERVICIOS E-MAIL' },
    {
      feature: 'Cuentas de e-mail',
      helpText: 'Cuentas de correo configurables',
      v: ['ILIMITADAS', 'ILIMITADAS', 'ILIMITADAS', 'ILIMITADAS']
    },
    { section: 'SERVICIOS GENERALES' },
    {
      feature: 'Dominios permitidos',
      helpText: 'Dominio principal de su sitio web',
      v: ['ILIMITADOS', 'ILIMITADOS', 'ILIMITADOS', 'ILIMITADOS']
    },
    {
      feature: 'Subdominios',
      helpText: 'Subdominio dentro de tu dominio principal',
      v: ['check', 'check', 'check', 'check']
    },
    {
      feature: 'Redireccionados',
      helpText: 'Páginas de redirección',
      v: ['check', 'check', 'check', 'check']
    },
    {
      feature: 'Cuentas FTP',
      helpText: 'Protocolo de transferencia de archivos',
      v: ['check', 'check', 'check', 'check']
    },
    { section: 'BASES DE DATOS' },
    {
      feature: 'Base de Datos',
      helpText: 'Conjunto de datos almacenados sistemáticamente',
      v: ['ILIMITADAS', 'ILIMITADAS', 'ILIMITADAS', 'ILIMITADAS']
    },
    {
      feature: 'MySQL y PostgreSQL',
      helpText: 'Sistemas de gestión de base de datos',
      v: ['check', 'check', 'check', 'check']
    },
    { section: 'SERVIDORES' },
    {
      feature: 'DNS anónimo y personalizable',
      helpText: 'Resolución entre nombres de host y direcciones de internet',
      v: ['check', 'check', 'check', 'check']
    },
    {
      feature: 'Discos redundantes RAID 10',
      helpText: 'Respaldan los discos de forma constante',
      v: ['check', 'check', 'check', 'check']
    },
    {
      feature: 'Mínimo 8 CPU Dual Quad Core Xeon',
      helpText: 'Característica de nuestros servidores',
      v: ['check', 'check', 'check', 'check']
    },
    { section: 'SOPORTE' },
    {
      feature: 'Soporte preferencial (chat, teléfono, ticket e-mail)',
      helpText: 'Soporte profesional 24/7',
      v: ['INCLUIDO', 'INCLUIDO', 'INCLUIDO', 'INCLUIDO']
    }
  ];

  constructor(
    private readonly locale: RackeoLocaleService,
    public readonly cart: RackeoCartService,
    private readonly meta: MetaService,
    private readonly canonical: CanonicalService
  ) {}

  ngOnInit(): void {
    this.comparisonRows = this.comparisonData;
    this.meta.setTitle('Reseller | Revendedor de Hosting | Rackeo');
    this.meta.setTag('description', `Planes Hosting Reseller con WHM y cPanel: espacio web, cuentas ilimitadas, Softaculous y Site Publisher. Revende hosting con Rackeo ${RACKEO_SITE.countryName}.`);
    this.meta.setTag('og:type', 'website');
    this.meta.setTag('og:image:type', 'image/png');
    this.meta.setTag('og:image', RACKEO_SITE.ogImageUrl);
    this.meta.setTag('og:image:width', '1200');
    this.meta.setTag('og:image:height', '1200');
    this.meta.setTag('og:image:alt', 'Reseller | Revendedor de Hosting | Rackeo');
    this.meta.setTag('og:locale', RACKEO_SITE.ogLocale);
    this.canonical.set('/hosting-reseller');

    this.locale.getContent().pipe(take(1)).subscribe((content) => {
      if (content?.hostingReseller) {
        this.applyHostingResellerFile(content.hostingReseller);
      }
    });
  }

  private applyHostingResellerFile(file: RackeoHostingResellerData): void {
    if (file.heroImage) {
      this.heroImage = file.heroImage;
    }
    if (file.heroAlt) {
      this.heroAlt = file.heroAlt;
    }
    this.plans = file.plans || [];
    this.comparisonPrices = file.comparisonPrices || [];
  }
  contractLabel(months: number): string {
    if (months === 1) {
      return '1 Mes';
    }
    return `${months} Meses`;
  }

  togglePlanDetails(planId: string): void {
    this.expandedPlanIds = {
      ...this.expandedPlanIds,
      [planId]: !this.expandedPlanIds[planId]
    };
  }

  isPlanDetailsOpen(planId: string): boolean {
    return !!this.expandedPlanIds[planId];
  }

  ariaToggleResellerPlan(plan: RackeoHostingResellerPlan): string {
    return this.isPlanDetailsOpen(plan.id)
      ? `Ocultar detalles de ${plan.name}`
      : `Ver detalles de ${plan.name}`;
  }

  setTab(i: number): void {
    if (i >= 0 && i < this.tabPills.length) {
      this.activeTab = i;
    }
  }

  comparisonTableColspan(): number {
    return 2 + this.plans.length;
  }
}
